import React from 'react';

import { uiConfig } from '@/config';
import { cn } from '@/libs';
import { AlertMessage, RadioGroupItem } from '@/ui-kit';

import { FieldError, useFormActions } from '../../core';
import { config } from '../../config';
import { useAddressOptionViews, useShouldValidateField } from '../../hooks';
import type { FieldsGroupAddressConfig } from '../../typings';
import {
  addressLinkFieldId,
  type AddressChoiceState,
} from '../../utils/addressLink';

import './AddressSourceChoice.scss';

type AddressSourceChoiceProps = {
  groupFieldId: string;
  groupTitle: string;
  address: FieldsGroupAddressConfig;
  choice: AddressChoiceState;
  disabled?: boolean;
};

/** Asks where an address group's address comes from: its own fields or another group's. */
export const AddressSourceChoice: React.FC<AddressSourceChoiceProps> = ({
  groupFieldId,
  groupTitle,
  address,
  choice,
  disabled,
}) => {
  const { setFieldValue } = useFormActions();
  const { views, linkedPoBoxMessage } = useAddressOptionViews(
    groupFieldId,
    address,
    choice,
  );
  const linkFieldId = addressLinkFieldId(groupFieldId);
  const noticeId = `${linkFieldId}_notice`;

  // Formik reinitialization puts back the seed, so this re-syncs on every drift.
  useShouldValidateField(
    groupFieldId,
    !choice.isLinked,
    config.addressFieldsShownFieldSuffix,
  );

  // The prompt defaults to the title, which the group already shows above.
  const prompt = address.prompt || groupTitle;

  return (
    <div className={cn('asc__container', uiConfig.grid.maxColumnsSpanClass)}>
      {prompt !== groupTitle && (
        <p className="asc__prompt">
          {prompt}
          {address.answerRequired && <span className="asc__required"> *</span>}
        </p>
      )}
      <div
        id={linkFieldId}
        role="radiogroup"
        aria-label={prompt}
        aria-required={address.answerRequired || undefined}
        aria-describedby={linkedPoBoxMessage ? noticeId : undefined}
        className="asc__options"
      >
        {views.map((view, index) => (
          <RadioGroupItem
            // Positions without an entry share a value, so key by index.
            key={index}
            id={`${linkFieldId}_${index}`}
            group={linkFieldId}
            label={view.text}
            value={view.value}
            scale="medium"
            checked={view.value === choice.selectedValue}
            disabled={disabled || !view.enabled}
            onChange={() => setFieldValue(linkFieldId, view.value, true)}
          />
        ))}
      </div>
      {/* Display-only: the server's progress rule is what blocks signing. */}
      {linkedPoBoxMessage && (
        <AlertMessage
          id={noticeId}
          className="alert-message-full-width"
          variant="warning"
          description={linkedPoBoxMessage}
        />
      )}
      <FieldError name={linkFieldId} />
    </div>
  );
};
